import React from 'react';
import CopyIcon from '@atlaskit/icon/core/copy';
import { fg } from '@atlassian/jira-feature-gating';
import { COMMAND_PALETTE_SECTIONS } from '@atlassian/jira-command-palette-common/src/common/constants.tsx';
import { COMMAND_PALETTE_QUICK_ACTIONS_PRIORITY } from '@atlassian/jira-command-palette-common/src/constants.tsx';
import { useCommandKeywords } from '@atlassian/jira-command-palette-common/src/controllers/use-command-keywords/index.tsx';
import {
	type Command,
	CommandActionType,
} from '@atlassian/jira-command-palette/src/common/types/commands/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useFlagsService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { messages } from './messages.tsx';
import { getWindow, getNavigator } from './utils.tsx';

export const useCopyPageUrlCommand = (): Command => {
	const { showFlag } = useFlagsService();
	const { formatMessage } = useIntl();

	const copyToClipboard = async () => {
		try {
			await getNavigator().clipboard.writeText(getWindow().location.href);
			showFlag({
				type: 'success',
				title: messages.successTitleCopiedUrl,
				messageId:
					'command-palette-integration.ui.initial-commands.use-copy-page-url.show-flag.success',
				messageType: 'transactional',
			});
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (err: any) {
			showFlag({
				type: 'error',
				title: messages.errorTitleCopiedUrl,
				description: messages.errorDescriptionCopiedUrl,
				messageId:
					'command-palette-integration.ui.initial-commands.use-copy-page-url.show-flag.error',
				messageType: 'transactional',
			});

			fireErrorAnalytics({
				error: err,
				meta: {
					id: 'commandPaletteCopyUrlToClipboard',
					packageName: 'jiraCommandPaletteIntegration',
					teamName: 'deliveroo',
				},
			});
		}
	};

	const { getKeywords } = useCommandKeywords();

	return {
		id: 'copy-page-url',
		name: formatMessage(messages.commandCommandLabel),
		keywords: getKeywords('copyCurrentPageUrlSynonyms'),
		primaryAction: {
			type: CommandActionType.PERFORM,
			perform: copyToClipboard,
		},
		section: COMMAND_PALETTE_SECTIONS.quickActions,
		priority: COMMAND_PALETTE_QUICK_ACTIONS_PRIORITY.copyUrl,
		components: {
			LeftIcon: () => (
				<CopyIcon
					spacing="spacious"
					label={
						fg('deliveroo_compal_a11y_fixes') ? '' : formatMessage(messages.commandCommandLabel)
					}
					aria-hidden={fg('deliveroo_compal_a11y_fixes') ? true : undefined}
				/>
			),
		},
		analytics: {
			action: 'copyPageURL',
		},
	};
};
